import type { LoginRequestPayload } from '@vy/api-definition';
import type { Role } from '@vy/types';
import {
  getLocalStorageString,
  getRequiredLocalStorageString,
  localStorageExists,
  removeLocalStorageEntry,
  setLocalStorageString,
} from '../../utils/local-storage';
import { getPublicApi } from '../api';
import { ACCOUNT_ID_KEY, ROLE_KEY, TOKEN_KEY, USER_ID_KEY } from './user-auth-consts';

export async function login(loginRequest: LoginRequestPayload) {
  const api = getPublicApi();

  const loginResponse = await api.login(
    { loginRequest },
    {
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Credentials': 'true',
      },
      credentials: 'include',
    },
  );

  storeAccountId(loginResponse.user.accountId);
  storeUserRole(loginResponse.user.role);
  storeUserId(loginResponse.user.id);
  storeToken(loginResponse.token);
}

export async function logout() {
  await revokeServerSession();

  deleteToken();
  location.reload();
}

export async function revokeServerSession(): Promise<void> {
  try {
    const token = getToken();

    if (token) {
      const basePath = import.meta.env.VITE_WEFT_API;

      await fetch(`${basePath}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
    }
  } catch {
    // Best-effort — proceed with client-side cleanup even if the server call fails
  }
}

export function isLoggedIn() {
  return localStorageExists(TOKEN_KEY);
}

export function getRequiredToken() {
  return getRequiredLocalStorageString(TOKEN_KEY);
}

export function getToken() {
  return getLocalStorageString(TOKEN_KEY);
}

export function getAccountId() {
  return getLocalStorageString(ACCOUNT_ID_KEY);
}

export function getUserId() {
  return getLocalStorageString(USER_ID_KEY);
}

export function storeToken(token: string) {
  setLocalStorageString(TOKEN_KEY, token);
}

export function storeAccountId(accountId: string) {
  setLocalStorageString(ACCOUNT_ID_KEY, accountId);
}

export function storeUserId(userId: string) {
  setLocalStorageString(USER_ID_KEY, userId);
}

export function storeUserRole(role: Role) {
  setLocalStorageString(ROLE_KEY, role);
}

function deleteToken() {
  removeLocalStorageEntry(TOKEN_KEY);
}

let inFlightRefresh: Promise<void> | null = null;

export async function refreshToken() {
  // Dedupe concurrent refreshes: when many requests 401 at once (e.g. an
  // expired token on page load), they share a single in-flight refresh
  // instead of each firing its own POST /auth/refresh-token.
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

async function performRefresh() {
  const api = getPublicApi();

  const { token } = await api.refreshToken({
    credentials: 'include',
  });

  storeToken(token);
}
